import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CalendarDays, Mail, Phone, Clock, User, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  // State for tracking selected date
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Site settings state
  const [siteSettings, setSiteSettings] = useState({
    booking_enabled: false,
    coming_soon_message: 'Coming Soon'
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Client contact form state
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  // Dynamic time slots based on day type
  const getTimeSlots = (date) => {
    if (!date) return [];
    
    const dayOfWeek = date.getDay(); // Sunday = 0, Saturday = 6
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isWeekend) {
      // Weekend: 10 AM - 3 PM
      return ['10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM'];
    } else {
      // Weekday: 5 PM - 9 PM
      return ['5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'];
    }
  };

  const currentTimeSlots = getTimeSlots(selectedDate);

  // Fetch site settings on component mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings`);
        setSiteSettings(response.data);
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Error fetching site settings:', error);
        // Use default settings if API fails
        setSettingsLoaded(true);
      }
    };

    fetchSiteSettings();
  }, []);

  const handleScheduleMeeting = () => {
    setShowBooking(!showBooking);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Clear selected time when date changes since time slots are different for weekdays/weekends
    setSelectedTime('');
  };

  const handleInputChange = (field, value) => {
    setClientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBooking = async () => {
    if (selectedDate && selectedTime && clientInfo.name && clientInfo.email) {
      try {
        setIsSubmitting(true);
        
        const bookingData = {
          client_name: clientInfo.name,
          client_email: clientInfo.email,
          client_phone: clientInfo.phone || null,
          company: clientInfo.company || null,
          message: clientInfo.message || null,
          meeting_date: selectedDate.toISOString().split('T')[0],
          meeting_time: selectedTime
        };
        
        const response = await axios.post(`${BACKEND_URL}/api/bookings`, bookingData);
        
        if (response.data.success) {
          // Show confirmation instead of alert
          setShowConfirmation(true);
          setShowBooking(false);
          
          // Reset form
          setSelectedDate(null);
          setSelectedTime('');
          setClientInfo({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: ''
          });
        } else {
          alert('Error scheduling meeting. Please try again.');
        }
      } catch (error) {
        console.error('Booking error:', error);
        if (error.response?.data?.detail) {
          alert(`Error: ${error.response.data.detail}`);
        } else {
          alert('Error scheduling meeting. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert('Please fill in your name, email, and select both a date and time for your meeting.');
    }
  };

  // Disable only past dates (weekends are now allowed)
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const contactOptions = [
    {
      icon: <CalendarDays size={32} className="calendar-pulse-animation" />,
      title: "Schedule a Meeting",
      description: "Book a consultation to discuss your project",
      action: siteSettings.booking_enabled ? "Schedule Now" : siteSettings.coming_soon_message,
      onClick: siteSettings.booking_enabled ? handleScheduleMeeting : null
    },
    {
      icon: <Mail size={32} className="email-bounce-animation" />,
      title: "Email Us",
      description: "Send us your project details",
      action: "admin@loveyuhlife.ca",
      onClick: () => window.location.href = 'mailto:admin@loveyuhlife.ca'
    },
    {
      icon: <Phone size={32} className="phone-ring-animation" />,
      title: "Call Us",
      description: "Speak directly with our team",
      action: "416 561 8684",
      onClick: null  // No click action - just display phone number
    }
  ];

  return (
    <section id="contact" className="section-large">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="heading-1 mb-6">Get In Touch</h2>
          <p className="body-medium max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Ready to transform your business? Choose the best way to connect with us and let's discuss your project.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 contact-options-grid">
          {contactOptions.map((option, index) => (
            <Card 
              key={index} 
              className={`testimonial-card text-center mobile-card-spacing ${
                option.onClick ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'
              }`} 
              onClick={option.onClick}
            >
              <CardHeader>
                <div className="flex justify-center mb-4" style={{ color: 'var(--brand-primary)' }}>
                  {option.icon}
                </div>
                <CardTitle className="heading-5">{option.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {option.description}
                </p>
                {/* Render button for Schedule and Email, text for Phone */}
                {option.title === "Call Us" ? (
                  <p className="text-lg font-semibold" style={{ color: 'var(--brand-primary)' }}>
                    {option.action}
                  </p>
                ) : (
                  <Button 
                    className={`btn-secondary ${option.onClick ? 'btn-hover-scale' : ''}`}
                    disabled={!option.onClick}
                  >
                    {option.action}
                  </Button>
                )}
                {index === 0 && !siteSettings.booking_enabled && (
                  <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                    {siteSettings.coming_soon_message}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Meeting Booking Interface */}
        {showBooking && (
          <Card className="max-w-6xl mx-auto" style={{ backgroundColor: 'var(--bg-card)' }}>
            <CardHeader>
              <CardTitle className="heading-3 text-center">Schedule Your Meeting</CardTitle>
              <p className="body-small text-center" style={{ color: 'var(--text-secondary)' }}>
                Fill in your details and select a date/time. We'll contact you with Teams or Zoom meeting details within 24 hours.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 contact-booking-grid">
                {/* Client Information Form */}
                <div className="lg:col-span-1">
                  <h4 className="heading-6 mb-4 flex items-center gap-2">
                    <User size={20} style={{ color: 'var(--brand-primary)' }} />
                    Contact Information
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="Your full name"
                        value={clientInfo.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full mobile-form-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        value={clientInfo.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full mobile-form-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        placeholder="(416) 555-0123"
                        value={clientInfo.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full mobile-form-input"
                      />
                    </div>
                    
                    <div>
                      <label className="body-small font-semibold mb-2 block" style={{ color: 'var(--text-primary)' }}>
                        Company/Organization
                      </label>
                      <Input
                        type="text"
                        placeholder="Your business name"
                        value={clientInfo.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="w-full mobile-form-input"
                      />
                    </div>
                    
                    <div>
                      <label className="body-small font-semibold mb-2 block flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        <MessageSquare size={16} />
                        Project Details
                      </label>
                      <Textarea
                        placeholder="Tell us about your project or what you'd like to discuss..."
                        value={clientInfo.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        className="w-full h-24 mobile-form-input"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Calendar */}
                <div className="lg:col-span-1">
                  <h4 className="heading-6 mb-4 flex items-center gap-2">
                    <CalendarDays size={20} style={{ color: 'var(--brand-primary)' }} />
                    Choose Date
                  </h4>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={isDateDisabled}
                    className="rounded-md border"
                    style={{ backgroundColor: 'var(--bg-page)' }}
                  />
                </div>

                {/* Time Slots */}
                <div className="lg:col-span-1">
                  <h4 className="heading-6 mb-2 flex items-center gap-2">
                    <Clock size={20} style={{ color: 'var(--brand-primary)' }} />
                    Available Times
                  </h4>
                  
                  {selectedDate && (
                    <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                      {selectedDate.getDay() === 0 || selectedDate.getDay() === 6 
                        ? "Weekend hours: 10:00 AM - 3:00 PM"
                        : "Weekday hours: 5:00 PM - 9:00 PM"
                      }
                    </p>
                  )}
                  
                  {!selectedDate && (
                    <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                      Please select a date first to see available times
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3 mb-6 time-slots-grid">
                    {currentTimeSlots.map((time) => (
                      <Button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`
                          ${selectedTime === time ? 'btn-primary' : 'btn-secondary'} 
                          btn-hover-scale text-sm py-3
                        `}
                      >
                        {time}
                      </Button>
                    ))}
                  </div>
                  
                  {selectedDate && selectedTime && clientInfo.name && clientInfo.email && (
                    <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--bg-light)', border: '1px solid var(--brand-primary)' }}>
                      <h5 className="body-small font-semibold mb-2" style={{ color: 'var(--brand-primary)' }}>
                        Meeting Summary:
                      </h5>
                      <p className="body-small" style={{ color: 'var(--text-primary)' }}>
                        <strong>Client:</strong> {clientInfo.name}<br/>
                        <strong>Email:</strong> {clientInfo.email}<br/>
                        <strong>Date:</strong> {selectedDate.toDateString()}<br/>
                        <strong>Time:</strong> {selectedTime}<br/>
                        {clientInfo.company && (
                          <>
                            <strong>Company:</strong> {clientInfo.company}<br/>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    onClick={handleBooking}
                    className="btn-primary btn-hover-scale w-full"
                    disabled={!selectedDate || !selectedTime || !clientInfo.name || !clientInfo.email || isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Meeting Request'}
                  </Button>
                  
                  <p className="body-small mt-3 text-center" style={{ color: 'var(--text-secondary)' }}>
                    We'll send you Teams/Zoom details within 24 hours
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <Card className="max-w-md mx-4" style={{ backgroundColor: 'var(--bg-page)' }}>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    <svg 
                      className="w-8 h-8" 
                      style={{ color: 'var(--text-inverse)' }}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                  </div>
                </div>
                <CardTitle className="heading-4" style={{ color: 'var(--brand-primary)' }}>
                  Meeting Request Submitted!
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="body-medium mb-4" style={{ color: 'var(--text-primary)' }}>
                  Thank you for scheduling a meeting with LOVEYUHLIFE!
                </p>
                
                <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: 'var(--bg-light)' }}>
                  <h4 className="heading-6 mb-2" style={{ color: 'var(--brand-primary)' }}>
                    What happens next?
                  </h4>
                  <ul className="body-small text-left space-y-2" style={{ color: 'var(--text-secondary)' }}>
                    <li>• We'll send you a confirmation email shortly</li>
                    <li>• Teams/Zoom meeting link within 24 hours</li>
                    <li>• Meeting reminders before your scheduled time</li>
                  </ul>
                </div>
                
                <p className="body-small mb-6" style={{ color: 'var(--text-secondary)' }}>
                  If you need to reschedule, please contact us at{' '}
                  <a 
                    href="mailto:admin@loveyuhlife.ca" 
                    className="link-text"
                  >
                    admin@loveyuhlife.ca
                  </a>
                </p>
                
                <Button 
                  onClick={() => setShowConfirmation(false)}
                  className="btn-primary btn-hover-scale w-full"
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactSection;